import { ref, computed } from 'vue';
import { jwtDecode } from 'jwt-decode';

// State to hold the decoded permissions (singleton pattern for the composable)
const userPermissions = ref([]);
const impersonatingState = ref(false);
const currentUserId = ref(null);

const API_BASE = process.env.VUE_APP_API_BASE || "https://admin.trueeye.co/api/v1";

export function usePermissions() {
  /**
   * Initializes permissions by fetching from backend /v1/auth/me or decoding token.
   * This ensures impersonated roles are correctly applied in the UI.
   */
  const initPermissions = async () => {
    const token = sessionStorage.getItem('token');
    const isImpersonatingActive = !!sessionStorage.getItem('impersonating_user_id');

    if (!token) {
      userPermissions.value = [];
      impersonatingState.value = false;
      return;
    }

    try {
      // 1. Fetch fresh identity from backend (aware of impersonation via headers)
      const response = await fetch(`${API_BASE}/auth/me`, {

        headers: {
          'Authorization': `Bearer ${token}`,
          'X-Impersonate-User': sessionStorage.getItem('impersonating_user_id') || ''
        }
      });

      if (response.ok) {
        const data = await response.json();
        userPermissions.value = data.roles || [];
        impersonatingState.value = !!data.isImpersonating;
        currentUserId.value = data.id;
        return;
      } else {
        console.error('[usePermissions] /auth/me failed with status:', response.status);
      }
    } catch (err) {
      console.error('[usePermissions] Connection error fetching /auth/me:', err);
    }

    // 2. Fallback - ONLY if NOT impersonating
    // If we are impersonating, we MUST NOT fallback to the local token because it contains the Admin's roles
    if (isImpersonatingActive) {
      console.warn('[usePermissions] Impersonation is active but /auth/me failed. Clearing permissions for safety.');
      userPermissions.value = [];
      impersonatingState.value = true;
      return;
    }

    try {
      const decoded = jwtDecode(token);
      const realmRoles = decoded.realm_access?.roles || [];
      const clientRoles = decoded.resource_access?.['TRUEEYE_SERVICE']?.roles || [];
      const allRoles = new Set([...realmRoles, ...clientRoles]);
      userPermissions.value = Array.from(allRoles);
      impersonatingState.value = false;
    } catch (error) {
      console.error('[usePermissions] Failed to decode token:', error);
      userPermissions.value = [];
    }
  };

  // Helper to check for global bypass roles
  const _hasBypassRole = () => {
    const bypassRoles = ['superadmin', 'super_admin', 'admin', 'platform_admin', 'trueeye_admin'];
    return userPermissions.value.some(p => bypassRoles.includes(p.toLowerCase()));
  };

  /**
   * Checks if the user has a specific permission.
   * @param {string} permission - The permission string to check
   * @returns {boolean}
   */
  const hasPermission = (permission) => {
    if (_hasBypassRole()) return true;
    return userPermissions.value.includes(permission);
  };

  /**
   * Checks if the user has AT LEAST ONE of the provided permissions.
   * @param {string[]} permissions - Array of permissions to check
   * @returns {boolean}
   */
  const hasAnyPermission = (permissions) => {
    if (_hasBypassRole()) return true;
    if (!Array.isArray(permissions)) return hasPermission(permissions);
    
    return permissions.some(p => userPermissions.value.includes(p));
  };

  /**
   * Checks if the user has ALL of the provided permissions.
   * @param {string[]} permissions - Array of permissions to check
   * @returns {boolean}
   */
  const hasAllPermissions = (permissions) => {
    if (_hasBypassRole()) return true;
    if (!Array.isArray(permissions)) return hasPermission(permissions);

    return permissions.every(p => userPermissions.value.includes(p));
  };

  return {
    permissions: computed(() => userPermissions.value),
    isImpersonating: computed(() => impersonatingState.value),
    userId: computed(() => currentUserId.value),
    initPermissions,
    hasPermission,
    hasAnyPermission,
    hasAllPermissions
  };
}
